import { type MapStore, onNotify, type WritableAtom } from 'nanostores';

import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { isObject } from '@iconik-io/core/src/is_object';

import { throttle } from '../utils/throttle.js';
import type { SyncSettings } from './client/sync_client.js';

const logger = DebugLogger.getInstance('unified_player_sync_client');

/**
 * Base properties and methods for synchronized stores, designed for managing state in a distributed system.
 * These properties integrate synchronization metadata and methods to handle keeping the state
 * consistent across multiple clients using communication with a central WebSocket server.
 */
export type SyncBase<Value> = {
  /**
   * A unique name identifying this store within the synchronization system.
   */
  name: string;

  /**
   * Marks a specific state change as confirmed by the server.
   *
   * @param commandId - The ID of the command to confirm.
   * @returns `true` if the command was successfully confirmed, `false` otherwise.
   */
  setConfirmed: (commandId: string) => boolean;

  /**
   * Marks a specific state change as unconfirmed.
   *
   * @param commandId - The ID of the command to mark as unconfirmed.
   * @param key - Optional key for map stores to mark specific key changes.
   */
  setUnconfirmed: (commandId: string, key?: string) => void;

  /**
   * Get the bootstrap value of this store, used when asked for bootstrap data
   */
  bootstrapValue: () => Value;

  /**
   * Sets the last full set timestamp.
   * Used in sync maps to track when to ignore key syncs.
   */
  setLastSetTimestamp: (lastSetTimestamp: number) => void;

  /**
   * The last full set timestamp.
   * Used in sync maps to track when to ignore key syncs.
   */
  lastSetTimestamp: number;
};

/**
 * A synchronized atom store for managing simple values.
 */
export type SyncAtom<Value = any> = SyncBase<Value> &
  WritableAtom<Value> & {
    unconfirmed: string | undefined;
  };

/**
 * A synchronized map store for managing key-value pairs.
 */
export type SyncMap<Value extends object = Record<string, unknown>> =
  SyncBase<Value> &
    MapStore<Value> & {
      unconfirmed: string | Record<string, string> | undefined;
    };

/**
 * Union type for all synchronized stores
 */
export type SyncStore = SyncAtom | SyncMap;

/**
 * Creates the common sync properties for a store
 */
function createSyncProperties<
  Value,
  Store extends
    | WritableAtom<Value>
    | MapStore<Value extends object ? Value : never>,
>(
  name: string,
  store: WritableAtom | MapStore,
  bootstrapStore?: WritableAtom<Value>,
): Store extends MapStore ? SyncMap : SyncAtom {
  const syncProperties: SyncBase<Value> & {
    unconfirmed: string | Record<string, string> | undefined;
  } = {
    name,
    unconfirmed: undefined,
    setConfirmed(commandId: string) {
      if (this.unconfirmed === undefined) {
        return false;
      }
      let found = false;
      if (typeof this.unconfirmed === 'string') {
        // Confirm the full object sync to be processed
        if (this.unconfirmed === commandId) {
          this.unconfirmed = undefined;
          found = true;
        }
        return found;
      }
      // Confirm the key syncs to be processed
      for (const key of Object.keys(this.unconfirmed)) {
        if (this.unconfirmed[key] === commandId) {
          delete this.unconfirmed[key];
          found = true;
        }
      }
      if (Object.keys(this.unconfirmed).length === 0) {
        this.unconfirmed = undefined;
      }

      return found;
    },
    setUnconfirmed(commandId: string, key?: string) {
      if (key) {
        this.unconfirmed = {
          ...(typeof this.unconfirmed === 'object' ? this.unconfirmed : {}),
          [key]: commandId,
        };
      } else {
        this.unconfirmed = commandId;
      }
    },
    bootstrapValue() {
      return bootstrapStore ? bootstrapStore.get() : store.get();
    },
    lastSetTimestamp: 0,
    setLastSetTimestamp(lastSetTimestamp: number) {
      this.lastSetTimestamp = lastSetTimestamp;
    },
  };
  // biome-ignore lint/plugin: the assigned store is narrowed to the generic conditional return type (Store extends MapStore ? SyncMap : SyncAtom), which TypeScript cannot infer from the concrete Object.assign result.
  return Object.assign(store, syncProperties) as Store extends MapStore
    ? SyncMap
    : SyncAtom;
}

/**
 * Adds synchronization capabilities to a store.
 * The store is designed for use with a websocket system to reach eventual consistency.
 * The communication code is in the `SyncClient` class and is instantiated in `sync_globals.ts`.
 *
 * Based on the technical specifications available here: https://docs.google.com/document/d/1_8eZ2SPAAxWj_uF9Utrhq1UmzdH-ZJwCArk2-zcnPGM/edit?tab=t.0
 *
 * @template Value - The type of the value stored in the store.
 * @template Store - The type of store being synchronized (WritableAtom or MapStore).
 *
 * @param store - The store to add synchronization to.
 * @param name - A unique name identifying this store for synchronization.
 * @param syncSettings - Contains syncClient and optional bootstrapStore.
 *
 * @returns A synchronized store instance with synchronization metadata and methods.
 *
 * ### Synchronization Behavior
 * - The `isSyncing` flag is set to `true` when processing updates received through the websocket (Inside the `SyncClient` class) and unset when done.
 *    - if there are pending local changes, incoming remote changes are ignored.
 *    - if a store change is made as a side effect while isSyncing is true, don't send sync updates for that side effect.
 * - Local changes are immediately reflected and throttled to the server
 * - Remote changes are ignored if there are unconfirmed local changes.
 */
export function addSyncCapabilities<
  Value,
  Store extends WritableAtom | MapStore,
>(
  store: Store,
  name: string,
  syncSettings: SyncSettings<Store>,
): Store extends MapStore<infer M> ? SyncMap<M> : SyncAtom<Value> {
  // Create and assign sync properties
  const { bootstrapStore, syncClient } = syncSettings;

  const syncStore = createSyncProperties(name, store, bootstrapStore);

  // Override set method to handle synchronization
  // biome-ignore lint/plugin: syncStore has the deferred conditional type SyncMap | SyncAtom, whose set methods form a union; calling it with the generic Value is sound at runtime but cannot be expressed without a cast.
  const originalSet = syncStore.set as (newValue: Value) => void;

  syncStore.set = (newValue: Value) => {
    if (syncClient.isSyncing && typeof syncStore.unconfirmed === 'string') {
      // Ignore sync updates that have local unconfirmed changes for the full object
      return;
    }
    if (!syncClient.isSyncing) {
      syncStore.setLastSetTimestamp(syncClient.latestTimestamp);
    }
    logger.debug('Sync store set new value', name, newValue);
    originalSet(newValue);
  };

  // Right before notifying local listeners, send sync command to the server
  onNotify(syncStore, ({ changed }) => {
    if (!syncClient.$syncActive.get()) {
      return;
    }
    const throttledSync = throttle(
      (newValue: Value) => {
        if ('setKey' in syncStore) {
          // For maps, handle key-specific changes
          const changedKey = changed;
          if (changedKey && !syncClient.isSyncing && isObject(newValue)) {
            // If a specific key changed, sync just that key's value
            const key = String(changedKey);
            syncClient?.sync(syncStore, newValue[key], key);
            return;
          }
        }
        // For atoms or full map updates
        syncClient?.sync(syncStore, newValue);
      },
      1000 / 25, // Max 25 fps
      true,
    );

    if (syncClient && !syncClient.isSyncing) {
      throttledSync(syncStore.get());
    }
  });

  syncClient.syncStores.set(name, syncStore);

  // biome-ignore lint/plugin: syncStore is narrowed to the generic conditional return type (Store extends MapStore<infer M> ? SyncMap<M> : SyncAtom<Value>), which TypeScript cannot infer from the concrete value.
  return syncStore as Store extends MapStore<infer M>
    ? SyncMap<M>
    : SyncAtom<Value>;
}
